import {
  type IImportProposal,
  type IImportProposalFields,
  type IImportProposedCustomSection,
  type IImportProposedQaPair,
  type IImportProposedValue,
  IMPORT_PROPOSAL_BOOLEAN_FIELDS,
  IMPORT_PROPOSAL_MAX_CUSTOM_SECTIONS,
  IMPORT_PROPOSAL_MAX_QA_PER_SECTION,
  IMPORT_PROPOSAL_PROSE_FIELDS,
  IMPORT_PROPOSAL_WINDOW_HOURS_FIELDS,
  KNOWLEDGE_BASE_MAX_CUSTOM_SECTION_TITLE_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH,
  KNOWLEDGE_BASE_MAX_TEXT_LENGTH,
  KNOWLEDGE_BASE_MAX_WINDOW_HOURS,
  KNOWLEDGE_BASE_MIN_WINDOW_HOURS,
} from '@principle-theorem/principle-core/interfaces';
import { clamp, compact, isNil } from 'lodash';
import { ImportProposal } from './import-proposal';

/**
 * Owns the caps the AI Receptionist provider accepts, which an Import
 * Proposal is clamped to, and the word-boundary cut that also shortens the
 * existing content a Website Import request sends.
 */
export class ImportProposalCaps {
  /**
   * Brings an extracted proposal within the caps the AI Receptionist provider
   * accepts, so any value the practice accepts can be pushed without the whole
   * push being rejected. A value that cannot be made valid is dropped rather
   * than proposed as a blank.
   */
  static clamp(raw: IImportProposal): IImportProposal {
    return {
      ...raw,
      fields: ImportProposalCaps._clampFields(raw.fields),
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        raw.sectionQaPairs,
        (pairs) => ImportProposalCaps._clampQaPairs(pairs)
      ),
      customSections: compact(
        raw.customSections.map((section) =>
          ImportProposalCaps._clampCustomSection(section)
        )
      ).slice(0, IMPORT_PROPOSAL_MAX_CUSTOM_SECTIONS),
    };
  }

  /**
   * Trims `text` and, when it is still longer than `maxLength`, cuts it back
   * to the last whole word that fits, so a clamped value never ends mid-word.
   * A single word longer than the cap is hard cut, because the cap must hold.
   */
  static clampTextAtWordBoundary(text: string, maxLength: number): string {
    const trimmed = text.trim();
    if (trimmed.length <= maxLength) {
      return trimmed;
    }
    const hardCut = trimmed.slice(0, maxLength);
    if (/\s/.test(trimmed.charAt(maxLength))) {
      return hardCut.trimEnd();
    }
    const lastWhitespaceIndex = hardCut.search(/\s\S*$/);
    return lastWhitespaceIndex === -1
      ? hardCut
      : hardCut.slice(0, lastWhitespaceIndex).trimEnd();
  }

  private static _clampFields(
    fields: IImportProposalFields
  ): IImportProposalFields {
    const clamped: IImportProposalFields = {};
    IMPORT_PROPOSAL_PROSE_FIELDS.forEach((field) =>
      ImportProposal.assignIfDefined(
        clamped,
        field,
        ImportProposalCaps._clampProposedText(
          fields[field],
          KNOWLEDGE_BASE_MAX_TEXT_LENGTH
        )
      )
    );
    IMPORT_PROPOSAL_BOOLEAN_FIELDS.forEach((field) =>
      ImportProposal.assignIfDefined(clamped, field, fields[field])
    );
    IMPORT_PROPOSAL_WINDOW_HOURS_FIELDS.forEach((field) =>
      ImportProposal.assignIfDefined(
        clamped,
        field,
        ImportProposalCaps._clampWindowHours(fields[field])
      )
    );
    return clamped;
  }

  private static _clampProposedText(
    proposed: IImportProposedValue<string> | undefined,
    maxLength: number
  ): IImportProposedValue<string> | undefined {
    if (!proposed) {
      return undefined;
    }
    const value = ImportProposalCaps.clampTextAtWordBoundary(
      proposed.value,
      maxLength
    );
    return value ? { value, provenance: proposed.provenance } : undefined;
  }

  /**
   * Only finite numbers are accepted: turning text such as "2 days" into hours
   * is the extractor's job, not a guess made here.
   */
  private static _clampWindowHours(
    proposed: IImportProposedValue<number> | undefined
  ): IImportProposedValue<number> | undefined {
    if (!proposed || !Number.isFinite(proposed.value)) {
      return undefined;
    }
    return {
      value: clamp(
        Math.round(proposed.value),
        KNOWLEDGE_BASE_MIN_WINDOW_HOURS,
        KNOWLEDGE_BASE_MAX_WINDOW_HOURS
      ),
      provenance: proposed.provenance,
    };
  }

  /**
   * The provider rejects a blank question or answer and a question over its
   * cap, so those pairs are dropped before the per-section limit is applied.
   * An over-long answer is shortened instead, as its opening still reads well.
   */
  private static _clampQaPairs(
    pairs: IImportProposedQaPair[]
  ): IImportProposedQaPair[] {
    return compact(
      pairs.map((pair) => ImportProposalCaps._clampQaPair(pair))
    ).slice(0, IMPORT_PROPOSAL_MAX_QA_PER_SECTION);
  }

  private static _clampQaPair(
    pair: IImportProposedQaPair
  ): IImportProposedQaPair | undefined {
    const question = pair.question.trim();
    const answer = ImportProposalCaps.clampTextAtWordBoundary(
      pair.answer,
      KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH
    );
    if (
      !question ||
      !answer ||
      question.length > KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH
    ) {
      return undefined;
    }
    return {
      question,
      answer,
      provenance: pair.provenance,
      ...(isNil(pair.existingId) ? {} : { existingId: pair.existingId }),
    };
  }

  /**
   * A custom section is a titled block of prose, so one missing either part
   * has nothing for the receptionist to say and is dropped, like a blank Q&A
   * pair.
   */
  private static _clampCustomSection(
    section: IImportProposedCustomSection
  ): IImportProposedCustomSection | undefined {
    const title = ImportProposalCaps.clampTextAtWordBoundary(
      section.title,
      KNOWLEDGE_BASE_MAX_CUSTOM_SECTION_TITLE_LENGTH
    );
    const content = ImportProposalCaps.clampTextAtWordBoundary(
      section.content,
      KNOWLEDGE_BASE_MAX_TEXT_LENGTH
    );
    if (!title || !content) {
      return undefined;
    }
    return {
      title,
      content,
      provenance: section.provenance,
      ...(section.qa
        ? { qa: ImportProposalCaps._clampQaPairs(section.qa) }
        : {}),
      ...(isNil(section.existingIndex)
        ? {}
        : { existingIndex: section.existingIndex }),
    };
  }
}
